import type { HitsNotification } from '@unifyapps/network/generated/models/hitsNotification';
import type { Query } from '@unifyapps/network/generated/models/query';
import {
  findUnreadNotifications,
  getFindUnreadNotificationsQueryKey,
} from '@unifyapps/network/generated/notification-rest-api/notification-rest-api';
import type {
  DefaultError,
  InfiniteData,
  QueryKey,
  QueryMeta,
} from '@unifyapps/network/react-query';
import { keepPreviousData, useInfiniteQuery } from '@unifyapps/network/react-query';

type PageParam = { page: { limit: number; cursor?: string } };

const getInitialPageParam = (limit: number): PageParam => ({
  page: { limit },
});

export const useUnreadNotificationsInfiniteQuery = (
  query: Query,
  pageSize = 30,
  queryOptions?: { queryKey?: QueryKey; enabled?: boolean },
) => {
  return useInfiniteQuery<
    HitsNotification,
    DefaultError,
    InfiniteData<HitsNotification>,
    QueryKey,
    PageParam
  >({
    queryKey: queryOptions?.queryKey ?? getFindUnreadNotificationsQueryKey(query),
    queryFn: ({ pageParam, meta }: { pageParam: PageParam | undefined; meta?: QueryMeta }) =>
      findUnreadNotifications(
        { ...query, ...(pageParam ?? getInitialPageParam(pageSize)) },
        { meta },
      ),
    getNextPageParam: (lastPageResponse, __, _) => {
      if (!lastPageResponse.cursor?.next) {
        return undefined;
      }

      return {
        page: {
          limit: pageSize,
          cursor: lastPageResponse.cursor?.next,
        },
      };
    },
    initialPageParam: getInitialPageParam(pageSize),
    placeholderData: keepPreviousData,
    enabled: queryOptions?.enabled,
  });
};
